import React from "react";
import { Box, Flex, Text, Avatar } from "@radix-ui/themes";
import { ReleaseConversation } from "@/v2/entities/release-conversation/ReleaseConversation.model";
import { TrainMessage, TrainMessageUpTo } from "@/v2/components/trainMessage";
import { SessionMetadataInline } from "./shared/SessionMetadataComponents";
import { useSimulator } from "../../context/SimulatorWrapper";

interface SessionMessagesProps {
  session: ReleaseConversation;
  onTrain?: (
    messages: Array<{ chatMessageId: string; text: string; author: string }>
  ) => void;
  onTrainUpTo?: (
    messages: Array<{ chatMessageId: string; text: string; author: string }>
  ) => void;
}

export const SessionMessages = ({
  session,
  onTrain,
  onTrainUpTo,
}: SessionMessagesProps) => {
  const { releasesWithVersions, topics } = useSimulator();

  const formatMessage = (message: string) => {
    if (message === "talk_to_an_agent") {
      return "Quack will escalate it to an agent";
    }
    return message;
  };

  const filteredComments = session.releaseComments?.filter(
    (comment: any) =>
      !comment.comment.toLowerCase().includes("reset") &&
      !comment.comment.includes("--")
  );

  if (!filteredComments || filteredComments.length === 0) {
    return <Box p="4">No messages found</Box>;
  }

  return (
    <Box>
      <SessionMetadataInline
        session={session}
        releasesWithVersions={releasesWithVersions}
        topics={topics}
        showVersion={true}
        showTopic={true}
        showFeedback={true}
        showPendingReview={true}
        showSubmitter={true}
        showInteractionType={true}
      />
      {filteredComments.map((comment: any, index: number) => (
        <Flex
          key={index}
          direction="column"
          style={{ alignItems: comment.isAgent ? "flex-end" : "flex-start" }}
          mb="4"
          p="2"
        >
          <Flex
            align="center"
            gap="2"
            style={{
              marginBottom: "8px",
              flexDirection: comment.isAgent ? "row-reverse" : "row",
            }}
          >
            <Avatar
              size="2"
              fallback={comment.isAgent ? "Q" : "U"}
              radius="full"
              style={{
                backgroundColor: comment.isAgent
                  ? "var(--amber-3)"
                  : "var(--gray-3)",
                color: comment.isAgent ? "var(--amber-11)" : "var(--gray-11)",
              }}
            />
            <Text size="2" weight="medium">
              {comment.isAgent ? "Quack" : "User"}
            </Text>
          </Flex>

          {(() => {
            const displayMessage = formatMessage(comment.comment);
            // Force RTL on any Hebrew/Arabic content so bubbles flip correctly.
            const isRtl = new RegExp("[\\u0590-\\u08FF\\uFB1D-\\uFDFF\\uFE70-\\uFEFF]").test(displayMessage || "");
            return (
              <Box
                dir={isRtl ? "rtl" : "ltr"}
                style={{
                  padding: "12px 16px",
                  borderRadius: "8px",
                  maxWidth: "80%",
                  backgroundColor: comment.isAgent
                    ? "white"
                    : "var(--gray-12)",
                  border: comment.isAgent ? "1px solid var(--gray-4)" : "none",
                  marginLeft: comment.isAgent ? "auto" : "16px",
                  marginRight: comment.isAgent ? "16px" : "auto",
                  position: "relative",
                  ...(isRtl
                    ? {
                        direction: "rtl" as const,
                        textAlign: "right" as const,
                      }
                    : null),
                }}
              >
                <Text
                  size="2"
                  style={{
                    color: comment.isAgent ? "var(--gray-12)" : "white",
                  }}
                >
                  {displayMessage}
                </Text>
              </Box>
            );
          })()}
          {(onTrain || onTrainUpTo) && (
            <Flex justify="end" gap="2" mt="2">
              {onTrainUpTo && index > 0 && !comment.isAgent && (
                <TrainMessageUpTo
                  onTrain={() =>
                    onTrainUpTo(
                      filteredComments.slice(0, index + 1).map((comment) => ({
                        chatMessageId: comment.id.toString(),
                        text: comment.comment,
                        author: comment.isAgent ? "QUACK" : "USER",
                      }))
                    )
                  }
                />
              )}
              {onTrain && (
                <TrainMessage
                  onTrain={() =>
                    onTrain([
                      {
                        chatMessageId: comment.id.toString(),
                        text: comment.comment,
                        author: "USER",
                      },
                    ])
                  }
                />
              )}
            </Flex>
          )}
        </Flex>
      ))}
    </Box>
  );
};
